/**
 * <complementary-products-component>
 *
 * Renders alongside the "Complementary products" theme block. Does not touch
 * the theme's existing add-to-cart flow in any way: it only listens for the
 * same PDP form submission and, if the shopper has checked one or more
 * add-ons, fires a second, independent request to add those add-on variants
 * to the cart. If nothing is checked, or this component isn't on the page,
 * this file has no effect and the theme's normal add-to-cart behavior runs
 * completely unmodified.
 */
class ComplementaryProductsComponent extends HTMLElement {
  /** @type {(event: SubmitEvent) => void} */
  _onSubmit = () => {};

  connectedCallback() {
    this._onSubmit = this.#handleSubmit.bind(this);
    // Capture phase: some cart/upsell apps intercept the PDP form's submit
    // event and call stopPropagation() once they've handled it (to run
    // their own AJAX add + drawer flow), which stops it from ever reaching
    // a bubble-phase listener here. Capture runs before any of that.
    document.addEventListener('submit', this._onSubmit, { capture: true });
    this.addEventListener('change', this.#handleCheckboxChange);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this._onSubmit, { capture: true });
    this.removeEventListener('change', this.#handleCheckboxChange);
  }

  /** @param {Event} event */
  #handleCheckboxChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.classList.contains('complementary-products__checkbox')) {
      return;
    }

    // console.log('[complementary-products] checkbox toggled', {
    //   variantId: checkbox.dataset.variantId,
    //   checked: checkbox.checked,
    // });
  };

  /** @param {SubmitEvent} event */
  #handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    // Only react to the PDP form that lives in the same section as this
    // block, so unrelated forms elsewhere on the page (quick add, other
    // product cards, newsletter signup, etc.) are never affected.
    const section = this.closest('.shopify-section');
    if (!section || !section.contains(form)) return;

    const checkedBoxes = Array.from(this.querySelectorAll('.complementary-products__checkbox:checked'));
    if (checkedBoxes.length === 0) return;

    const items = checkedBoxes
      .map((checkbox) => {
        const id = Number(checkbox.dataset.variantId);
        return id ? { id, quantity: 1 } : null;
      })
      .filter(Boolean);

    if (items.length === 0) return;

    checkedBoxes.forEach((checkbox) => {
      checkbox.disabled = true;
    });

    const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
    // console.log('[complementary-products] adding to cart on submit', items);

    fetch(cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response && response.status) {
          // console.warn('[complementary-products] Failed to add add-ons to cart:', response.message);
          checkedBoxes.forEach((checkbox) => {
            checkbox.disabled = false;
          });
          return;
        }

        // console.log('[complementary-products] added to cart', response);

        checkedBoxes.forEach((checkbox) => {
          checkbox.checked = false;
          checkbox.disabled = false;
        });

        const cartItemsComponent = document.querySelector('cart-items-component');
        if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
          cartItemsComponent.fetchCartData();
        }
      })
      .catch((error) => {
        // console.warn('[complementary-products] Network error adding add-ons to cart:', error);
        checkedBoxes.forEach((checkbox) => {
          checkbox.disabled = false;
        });
      });
  }
}

if (!customElements.get('complementary-products-component')) {
  customElements.define('complementary-products-component', ComplementaryProductsComponent);
}
